/**
 * QuestionnairesPage - List and manage async questionnaires
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  listQuestionnaires,
  deleteQuestionnaire,
  exportQuestionnaire,
} from '../services/questionnaireService';
import { Questionnaire, QuestionnaireStatus, SubscriptionTier } from '../types';
import MainLayout from '../components/MainLayout';
import UploadQuestionnaireModal from '../components/questionnaire/UploadQuestionnaireModal';
import './QuestionnairesPage.css';

const STATUS_BADGES: Record<QuestionnaireStatus, { label: string; className: string }> = {
  pending: { label: '⏳ Pending', className: 'status-pending' },
  processing: { label: '🔄 Processing', className: 'status-processing' },
  completed: { label: '✅ Completed', className: 'status-completed' },
  failed: { label: '❌ Failed', className: 'status-failed' },
};

const QuestionnairesPage: React.FC = () => {
  const { user, userContext, signOut } = useAuth();
  const navigate = useNavigate();
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const projectId = user?.project || '';
  const projectName = userContext?.project?.company_name || '';
  const userRole = user?.role || userContext?.membership?.role;
  const displayName = user?.fullName || userContext?.profile?.full_name || user?.email;
  const subscriptionTier = (userContext?.project?.subscription_tier || 'free') as SubscriptionTier;

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const loadQuestionnaires = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const result = await listQuestionnaires(projectId);
      setQuestionnaires(result.questionnaires);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load questionnaires');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadQuestionnaires();
  }, [loadQuestionnaires, refreshKey]);

  // Auto-refresh when there are processing questionnaires (only if tab is visible)
  useEffect(() => {
    const hasProcessing = questionnaires.some(q => q.status === 'processing');
    if (!hasProcessing) return;

    // Only poll if the tab is visible
    const poll = () => {
      if (document.visibilityState === 'visible') {
        setRefreshKey(k => k + 1);
      }
    };

    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [questionnaires]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this questionnaire?')) return;
    try {
      await deleteQuestionnaire(id);
      setRefreshKey(k => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete questionnaire');
    }
  };

  const handleExport = async (q: Questionnaire) => {
    try {
      await exportQuestionnaire(q.id, `${q.client_name}_${q.original_filename}`);
    } catch (err: any) {
      setError('Failed to export questionnaire');
    }
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    setRefreshKey(k => k + 1);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getProgress = (q: Questionnaire) => {
    if (q.total_items === 0) return 0;
    return Math.round((q.completed_items / q.total_items) * 100);
  };

  return (
    <MainLayout
      activeSection="questionnaires"
      onSectionChange={() => {}}
      projectName={projectName}
      projectId={projectId}
      username={displayName}
      userRole={userRole}
      subscriptionTier={subscriptionTier}
      onLogout={handleLogout}
    >
      <div className="questionnaires-page">
        <div className="page-header">
          <h1>📋 Questionnaires</h1>
          <button
            className="btn-primary"
            onClick={() => setShowUploadModal(true)}
          >
            📤 Upload Questionnaire
          </button>
        </div>

        {error && (
          <div className="error-banner">
            {error}
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {loading ? (
          <div className="loading-state">Loading questionnaires...</div>
        ) : questionnaires.length === 0 ? (
          <div className="empty-state">
            <p>No questionnaires yet. Upload a CSV to get started!</p>
            <button
              className="btn-primary"
              onClick={() => setShowUploadModal(true)}
            >
              📤 Upload Your First Questionnaire
            </button>
          </div>
        ) : (
          <div className="questionnaires-list">
            {questionnaires.map(q => (
              <div
                key={q.id}
                className="questionnaire-card"
                onClick={() => navigate(`/questionnaires/${q.id}`)}
              >
                <div className="card-header">
                  <h3>{q.client_name}</h3>
                  <span className={`status-badge ${STATUS_BADGES[q.status].className}`}>
                    {STATUS_BADGES[q.status].label}
                  </span>
                </div>
                <div className="card-meta">
                  <span>📁 {q.original_filename}</span>
                  <span>📊 {q.completed_items}/{q.total_items} items</span>
                  <span>📅 {formatDate(q.uploaded_at)}</span>
                  {(q.uploaded_by_name || q.uploaded_by) && (
                    <span title={q.uploaded_by}>👤 {q.uploaded_by_name || q.uploaded_by}</span>
                  )}
                </div>
                {q.status === 'processing' && (
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${getProgress(q)}%` }}
                    />
                    <span className="progress-text">{getProgress(q)}%</span>
                  </div>
                )}
                <div className="card-actions" onClick={e => e.stopPropagation()}>
                  <button onClick={() => handleExport(q)} title="Export CSV">
                    📥 Export
                  </button>
                  <button
                    className="btn-danger"
                    onClick={() => handleDelete(q.id)}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showUploadModal && (
          <UploadQuestionnaireModal
            projectId={projectId}
            onClose={() => setShowUploadModal(false)}
            onUploadComplete={handleUploadComplete}
            userId={user?.id}
            userName={user?.fullName || user?.email}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default QuestionnairesPage;

